import {
  APPS_PROBE_OWNER_ONLY,
  NEEDS_AGENT_ENVIRONMENT,
  detailOf,
  refusalText,
  statusOf,
  useContainerRuntime,
  useSaveContainerRuntime,
  useStartAppsProbe,
} from "@via/pages/apps/hpc/hpc-apps.api.tsx";
import {
  type AppsImagePolicy,
  type AppsMemoryEnforcement,
  type AppsProbeState,
  type RunEnvironmentContainerRuntimeResponseBody,
  type RunEnvironmentTransport,
  type StartRuntimeProbeResponseBody,
} from "common";
import { type JSX, type KeyboardEvent, useEffect, useState } from "react";

export const NO_RUNTIME_LINE =
  "Declare a container runtime on this environment before turning on apps";
export const NOT_EDITOR_LINE =
  "Only the run environment's owner, an admin of its organization or a platform admin can manage its apps settings.";
export const NODE_ALLOWLIST_HINT =
  "One entry per line: a host name suffix such as .cluster.example, or an address range such as 10.20.0.0/16. At least one address range (CIDR) is required, because the agent checks a node's address against ranges only.";
export const APPS_EGRESS_LINE =
  "An app shares the compute node's network: Foundry Connect does not restrict where it can connect.";

const PROBE_STATE_TEXT: Readonly<Record<AppsProbeState, string>> = {
  unmeasured: "Not run",
  pending: "Running",
  passed: "Passed",
  failed: "Failed",
};

const MEMORY_TEXT: Readonly<Record<AppsMemoryEnforcement, string>> = {
  enforced: "Enforced",
  not_enforced: "Not enforced",
  unknown: "Unknown",
};

/** The collapsed card's one line. */
export const appsCardSummary = (
  data: RunEnvironmentContainerRuntimeResponseBody | undefined
): string => {
  if (!data?.apps) return "Off";
  if (!data.apps.enabled) return "Off";
  return `On, app check ${PROBE_STATE_TEXT[data.apps.probe.state].toLowerCase()}`;
};

interface AppsForm {
  enabled: boolean;
  portLow: string;
  portHigh: string;
  nodeAllowlist: string;
  idleMinutes: string;
  startTimeoutMinutes: string;
  imagePolicy: AppsImagePolicy;
  imageList: string;
  imageRoot: string;
  requireIsolatedNode: boolean;
  maxCpus: string;
  maxMemoryMb: string;
  maxWallMinutes: string;
  maxLiveLaunches: string;
  maxLiveLaunchesPerUser: string;
}

const formFrom = (
  data: RunEnvironmentContainerRuntimeResponseBody
): AppsForm => {
  const apps = data.apps;
  const caps = data.appCaps;
  const text = (value: number | undefined): string =>
    value === undefined ? "" : String(value);
  return {
    enabled: apps?.enabled ?? false,
    portLow: text(apps?.portRange?.low),
    portHigh: text(apps?.portRange?.high),
    nodeAllowlist: (apps?.nodeAllowlist ?? []).join("\n"),
    idleMinutes: text(apps?.idleMinutes ?? 120),
    startTimeoutMinutes: text(apps?.startTimeoutMinutes ?? 10),
    imagePolicy: apps?.imagePolicy ?? "published_apps",
    imageList: (apps?.imageList ?? []).join("\n"),
    imageRoot: apps?.imageRoot ?? "",
    requireIsolatedNode: apps?.requireIsolatedNode ?? false,
    maxCpus: text(caps?.maxCpus),
    maxMemoryMb: text(caps?.maxMemoryMb),
    maxWallMinutes: text(caps?.maxWallMinutes ?? 1440),
    maxLiveLaunches: text(caps?.maxLiveLaunches),
    maxLiveLaunchesPerUser: text(caps?.maxLiveLaunchesPerUser),
  };
};

const lines = (value: string): string[] =>
  value
    .split("\n")
    .map(line => line.trim())
    .filter(line => line !== "");

/** A blank number is left out, so the stored value (or the default) stays. */
const optionalNumber = (value: string): number | undefined =>
  value.trim() === "" ? undefined : Number(value);

const swallowEnter = (event: KeyboardEvent<HTMLInputElement>): void => {
  if (event.key === "Enter") event.preventDefault();
};

const probeDetailText = (detail: unknown): string | null => {
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const record = detail as { detail?: unknown; reason?: unknown };
    if (typeof record.detail === "string") return record.detail;
    if (typeof record.reason === "string") return record.reason;
  }
  return null;
};

const probeOutcomeText = (outcome: StartRuntimeProbeResponseBody): string =>
  outcome.outcome === "submitted"
    ? `App check submitted as job ${outcome.jobId}.`
    : outcome.detail;

const inputClass =
  "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm";
const labelClass = "block text-sm font-medium leading-6 text-gray-900";
const secondaryButton =
  "rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";
const primaryButton =
  "rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * HPC app settings of a run environment (agent-9-1, spec 10). Apps run on agent environments only
 * (agent-d60), so an SSH environment gets one line and no fields. The fields are editable by
 * whoever the container runtime GET answers (the owner, an admin of the environment's organization
 * or a platform admin); anyone else gets a 403 and a line. A save sends the apps blocks and no
 * runtime fields, so the server keeps the stored runtime as it is (agent-d78). Every rule
 * (the CIDR one included) is the server's: a refusal is shown as the API words it.
 *
 * Like the Data folders card, nothing here is registered with the form: Save is its own PUT and
 * every button is type="button".
 */
export function AppsCard({
  environmentId,
  transport,
  ownerId,
  currentUserId,
}: {
  environmentId?: number;
  transport: RunEnvironmentTransport;
  ownerId?: number;
  currentUserId?: number;
}): JSX.Element {
  const isAgent = "agent" === transport;
  const runtime = useContainerRuntime(isAgent ? environmentId : undefined);
  const save = useSaveContainerRuntime(environmentId);
  const probe = useStartAppsProbe(environmentId);
  const [form, setForm] = useState<AppsForm | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [probeMessage, setProbeMessage] = useState<string | null>(null);

  useEffect(() => {
    if (runtime.data?.runtime) setForm(formFrom(runtime.data));
  }, [runtime.data]);

  if (!isAgent) {
    return <p className="text-sm text-gray-600">{NEEDS_AGENT_ENVIRONMENT}</p>;
  }
  if (!environmentId) {
    return (
      <p className="text-sm text-gray-600">
        Save this run environment first, then set up HPC apps on it.
      </p>
    );
  }
  if (runtime.isLoading) {
    return <p className="text-sm text-gray-600">Loading apps settings</p>;
  }
  if (runtime.isError) {
    return (
      <p className="text-sm text-gray-600">
        {statusOf(runtime.error) === 403
          ? NOT_EDITOR_LINE
          : "The apps settings could not be loaded."}
      </p>
    );
  }
  const data = runtime.data;
  if (!data?.runtime) {
    return <p className="text-sm text-gray-600">{NO_RUNTIME_LINE}</p>;
  }
  if (!form)
    return <p className="text-sm text-gray-600">Loading apps settings</p>;

  const isOwner = ownerId !== undefined && ownerId === currentUserId;
  const apps = data.apps;
  const probeState: AppsProbeState = apps?.probe.state ?? "unmeasured";

  const edit = (patch: Partial<AppsForm>): void => {
    setForm(previous => (previous ? { ...previous, ...patch } : previous));
    setSaveError(null);
    setSavedNotice(false);
  };

  const submit = async (): Promise<void> => {
    setSaveError(null);
    setSavedNotice(false);
    const portRange =
      form.portLow.trim() === "" && form.portHigh.trim() === ""
        ? null
        : { low: Number(form.portLow), high: Number(form.portHigh) };
    try {
      // The apps blocks only (agent-d78): the server keeps the stored runtime, so a copy of it read
      // before someone else's save can never overwrite theirs.
      await save.mutateAsync({
        apps: {
          enabled: form.enabled,
          portRange,
          nodeAllowlist: lines(form.nodeAllowlist),
          idleMinutes: optionalNumber(form.idleMinutes),
          startTimeoutMinutes: optionalNumber(form.startTimeoutMinutes),
          imagePolicy: form.imagePolicy,
          imageList: lines(form.imageList),
          // An empty field clears the folder; the server checks the path.
          imageRoot:
            form.imageRoot.trim() === "" ? null : form.imageRoot.trim(),
          requireIsolatedNode: form.requireIsolatedNode,
        },
        appCaps: {
          maxCpus: optionalNumber(form.maxCpus),
          maxMemoryMb: optionalNumber(form.maxMemoryMb),
          maxWallMinutes: optionalNumber(form.maxWallMinutes),
          maxLiveLaunches: optionalNumber(form.maxLiveLaunches),
          maxLiveLaunchesPerUser: optionalNumber(form.maxLiveLaunchesPerUser),
        },
      });
      setSavedNotice(true);
    } catch (failure) {
      setSaveError(
        detailOf(failure) ?? "The apps settings could not be saved."
      );
    }
  };

  const runCheck = async (): Promise<void> => {
    setProbeMessage(null);
    try {
      setProbeMessage(probeOutcomeText(await probe.mutateAsync()));
    } catch (failure) {
      setProbeMessage(refusalText(failure, "The app check could not start."));
    }
  };

  const numberField = (
    id: string,
    label: string,
    key: keyof AppsForm,
    hint?: string
  ): JSX.Element => (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        className={`mt-1 ${inputClass}`}
        value={form[key] as string}
        onKeyDown={swallowEnter}
        onChange={event => edit({ [key]: event.target.value })}
      />
      {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
    </div>
  );

  const probeDetail =
    probeState === "failed" ? probeDetailText(apps?.probe.detail) : null;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <input
          id="apps-enabled"
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-indigo-600"
          checked={form.enabled}
          onChange={event => edit({ enabled: event.target.checked })}
        />
        <label htmlFor="apps-enabled" className="text-sm text-gray-900">
          Allow HPC apps on this environment
        </label>
      </div>
      <p className="text-xs text-gray-600">{APPS_EGRESS_LINE}</p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {numberField("apps-port-low", "Port range low", "portLow")}
        {numberField("apps-port-high", "Port range high", "portHigh")}
      </div>

      <div>
        <label htmlFor="apps-node-allowlist" className={labelClass}>
          Node allowlist
        </label>
        <textarea
          id="apps-node-allowlist"
          rows={3}
          className={`mt-1 font-mono ${inputClass}`}
          placeholder={"10.20.0.0/16\n.cluster.example"}
          value={form.nodeAllowlist}
          onChange={event => edit({ nodeAllowlist: event.target.value })}
        />
        <p className="mt-1 text-xs text-gray-500">{NODE_ALLOWLIST_HINT}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {numberField(
          "apps-idle",
          "Idle limit (minutes)",
          "idleMinutes",
          "An app with no traffic for this long is stopped."
        )}
        {numberField(
          "apps-start-timeout",
          "Start timeout (minutes)",
          "startTimeoutMinutes",
          "An app not answering this long after its job starts is stopped."
        )}
      </div>

      <div>
        <label htmlFor="apps-image-policy" className={labelClass}>
          Image policy
        </label>
        <select
          id="apps-image-policy"
          className={`mt-1 ${inputClass}`}
          value={form.imagePolicy}
          onChange={event =>
            edit({ imagePolicy: event.target.value as AppsImagePolicy })
          }>
          <option value="published_apps">Images of published apps</option>
          <option value="listed">Only the images listed below</option>
        </select>
      </div>
      {form.imagePolicy === "listed" ? (
        <div>
          <label htmlFor="apps-image-list" className={labelClass}>
            Allowed images
          </label>
          <textarea
            id="apps-image-list"
            rows={3}
            className={`mt-1 font-mono ${inputClass}`}
            value={form.imageList}
            onChange={event => edit({ imageList: event.target.value })}
          />
          <p className="mt-1 text-xs text-gray-500">One image per line.</p>
        </div>
      ) : null}

      <div>
        <label htmlFor="apps-image-root" className={labelClass}>
          App image folder
        </label>
        <input
          id="apps-image-root"
          type="text"
          className={`mt-1 font-mono ${inputClass}`}
          placeholder="/shared/foundry/app-images"
          value={form.imageRoot}
          onChange={event => edit({ imageRoot: event.target.value })}
        />
        <p className="mt-1 text-xs text-gray-500">
          The folder on the cluster where apps that name a ready made .sif image
          are read from. It must be readable by every user who launches apps
          here, and writable only by admins. Leave it empty to run only apps
          that pull their image.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="apps-isolated-node"
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-indigo-600"
          checked={form.requireIsolatedNode}
          onChange={event =>
            edit({ requireIsolatedNode: event.target.checked })
          }
        />
        <label htmlFor="apps-isolated-node" className="text-sm text-gray-900">
          Reserve a whole node for each app
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {numberField("apps-max-cpus", "Max cpus per launch", "maxCpus")}
        {numberField(
          "apps-max-memory",
          "Max memory per launch (MB)",
          "maxMemoryMb"
        )}
        {numberField(
          "apps-max-wall",
          "Max walltime per launch (minutes)",
          "maxWallMinutes",
          "At most 10080 (one week)."
        )}
        {numberField("apps-max-live", "Max live launches", "maxLiveLaunches")}
        {numberField(
          "apps-max-live-per-user",
          "Max live launches per user",
          "maxLiveLaunchesPerUser"
        )}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          className={primaryButton}
          disabled={save.isPending}
          onClick={submit}>
          Save apps settings
        </button>
        {savedNotice ? (
          <span className="text-sm text-gray-600">Apps settings saved.</span>
        ) : null}
      </div>
      {saveError ? (
        <p className="text-sm text-red-600" role="alert">
          {saveError}
        </p>
      ) : null}

      <div className="space-y-2 border-t border-gray-100 pt-4">
        <div
          data-testid="apps-check-result"
          className="space-y-1 text-sm text-gray-700">
          <p>App check: {PROBE_STATE_TEXT[probeState]}</p>
          <p>Reached the node through: {apps?.networkMode ?? "Not measured"}</p>
          <p>
            Memory limits: {MEMORY_TEXT[apps?.memoryEnforcement ?? "unknown"]}
          </p>
          {apps?.probe.at ? (
            <p className="text-xs text-gray-500">
              Checked {new Date(apps.probe.at).toLocaleString()}
            </p>
          ) : null}
          {probeDetail ? <p className="text-red-600">{probeDetail}</p> : null}
        </div>
        <button
          type="button"
          className={secondaryButton}
          disabled={!isOwner || probe.isPending || probeState === "pending"}
          onClick={runCheck}>
          Run app check
        </button>
        {!isOwner ? (
          <p className="text-xs text-gray-600">{APPS_PROBE_OWNER_ONLY}</p>
        ) : null}
        {probeMessage ? (
          <p className="text-sm text-gray-700">{probeMessage}</p>
        ) : null}
      </div>
    </div>
  );
}
